import { Calculator, Check, Copy, DollarSign, Loader2 } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FY_OPTIONS,
  formatMonthLabel,
  getCurrentFiscalQuarter,
} from "@/components/deal-metrics/shared";
import { savedFieldClass, useSaveFlash } from "@/components/owners/goals/shared";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { fiscalYearMonths, formatFY } from "@/lib/fiscal";

interface ConsultingRow {
  month: string;
  engagementRingFee: number;
  defaultFee: number;
}

interface SourceOverheadResponse {
  sources: string[];
  months: string[];
  rows: { source: string; month: string; amount: number }[];
}

function parseMoney(raw: string): number | null {
  const cleaned = raw.replace(/[$,\s]/g, "");
  if (cleaned === "") return 0;
  const n = Number.parseFloat(cleaned);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function formatMoneyInput(n: number): string {
  return n === 0 ? "" : String(n);
}

export function ExpensesPage() {
  const { fy: currentFy } = getCurrentFiscalQuarter();
  const [selectedFy, setSelectedFy] = useState(currentFy);
  const fyMonths = useMemo(() => fiscalYearMonths(selectedFy), [selectedFy]);

  const [consultingRows, setConsultingRows] = useState<ConsultingRow[]>([]);
  const [overhead, setOverhead] = useState<SourceOverheadResponse>({
    sources: [],
    months: [],
    rows: [],
  });
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const { savedField, flashSaved } = useSaveFlash();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [feesRes, overheadRes] = await Promise.all([
        fetch(`/api/expenses/consulting-fees?fiscalYear=${selectedFy}`),
        fetch(`/api/expenses/source-overhead?fiscalYear=${selectedFy}`),
      ]);
      if (feesRes.ok) {
        const data = await feesRes.json();
        setConsultingRows(data.rows);
      }
      if (overheadRes.ok) {
        const data: SourceOverheadResponse = await overheadRes.json();
        setOverhead(data);
        setSelectedSource((prev) => {
          if (prev && data.sources.includes(prev)) return prev;
          return data.sources[0] ?? null;
        });
      }
    } finally {
      setLoading(false);
    }
  }, [selectedFy]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const saveConsulting = async (
    month: string,
    engagementRingFee: number,
    defaultFee: number,
    field: string,
  ) => {
    const res = await fetch("/api/expenses/consulting-fees", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fiscalYear: selectedFy, month, engagementRingFee, defaultFee }),
    });
    if (res.ok) {
      setConsultingRows((rows) =>
        rows.map((r) => (r.month === month ? { ...r, engagementRingFee, defaultFee } : r)),
      );
      flashSaved(field);
    }
  };

  const bulkConsulting = async (fromMonth: string) => {
    const row = consultingRows.find((r) => r.month === fromMonth);
    if (!row) return;
    const idx = fyMonths.indexOf(fromMonth);
    if (idx < 0) return;
    const remaining = fyMonths.slice(idx);
    const res = await fetch("/api/expenses/consulting-fees/bulk", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fiscalYear: selectedFy,
        months: remaining,
        engagementRingFee: row.engagementRingFee,
        defaultFee: row.defaultFee,
      }),
    });
    if (res.ok) {
      setConsultingRows((rows) =>
        rows.map((r) =>
          remaining.includes(r.month)
            ? { ...r, engagementRingFee: row.engagementRingFee, defaultFee: row.defaultFee }
            : r,
        ),
      );
      for (const m of remaining) {
        flashSaved(`consulting-${m}-er`);
        flashSaved(`consulting-${m}-other`);
      }
    }
  };

  const saveOverhead = async (source: string, month: string, amount: number, field: string) => {
    const res = await fetch("/api/expenses/source-overhead", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ source, fiscalYear: selectedFy, month, amount }),
    });
    if (res.ok) {
      setOverhead((prev) => ({
        ...prev,
        rows: prev.rows.map((r) =>
          r.source === source && r.month === month ? { ...r, amount } : r,
        ),
      }));
      flashSaved(field);
    }
  };

  const bulkOverhead = async (source: string, fromMonth: string) => {
    const row = overhead.rows.find((r) => r.source === source && r.month === fromMonth);
    if (!row) return;
    const idx = fyMonths.indexOf(fromMonth);
    if (idx < 0) return;
    const remaining = fyMonths.slice(idx);
    const res = await fetch("/api/expenses/source-overhead/bulk", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        source,
        fiscalYear: selectedFy,
        months: remaining,
        amount: row.amount,
      }),
    });
    if (res.ok) {
      setOverhead((prev) => ({
        ...prev,
        rows: prev.rows.map((r) =>
          r.source === source && remaining.includes(r.month) ? { ...r, amount: row.amount } : r,
        ),
      }));
      for (const m of remaining) flashSaved(`overhead-${source}-${m}`);
    }
  };

  const overheadForSource = useMemo(() => {
    if (!selectedSource) return new Map<string, number>();
    const map = new Map<string, number>();
    for (const r of overhead.rows) {
      if (r.source === selectedSource) map.set(r.month, r.amount);
    }
    return map;
  }, [overhead.rows, selectedSource]);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <PageHeader
            title="Expenses"
            description="Monthly costs used to calculate Digital Customer Acquisition Cost (CAC)."
          />
          <Select value={String(selectedFy)} onValueChange={(v) => setSelectedFy(Number(v))}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FY_OPTIONS.map((fy) => (
                <SelectItem key={fy} value={String(fy)}>
                  {formatFY(fy)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Tabs defaultValue="cac">
          <TabsList>
            <TabsTrigger value="cac">
              <DollarSign className="mr-2 h-4 w-4" />
              CAC Expenses
            </TabsTrigger>
            <TabsTrigger value="gpm-costs">
              <Calculator className="mr-2 h-4 w-4" />
              GPM Costs
            </TabsTrigger>
          </TabsList>

          <TabsContent value="cac" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Consulting Fees</CardTitle>
                <CardDescription>
                  Per-closed-deal consulting cost by product type. Applied to every closed digital
                  deal in the CAC calculation.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="h-64 bg-muted rounded animate-pulse" />
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[120px]">Month</TableHead>
                        <TableHead>Engagement Ring Fee</TableHead>
                        <TableHead>Other Product Fee</TableHead>
                        <TableHead className="w-[180px]" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {consultingRows.map((row) => (
                        <ConsultingMonthRow
                          key={row.month}
                          row={row}
                          savedField={savedField}
                          onSave={saveConsulting}
                          onBulk={() => bulkConsulting(row.month)}
                        />
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Source Overhead</CardTitle>
                <CardDescription>
                  Monthly cost to run each channel. Only overhead for an owner's configured Digital
                  Sources flows into their Digital CAC.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="h-64 bg-muted rounded animate-pulse" />
                ) : overhead.sources.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No sources found in deal data. Run a deal sync first.
                  </p>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Label className="text-xs text-muted-foreground uppercase tracking-wider">
                        Source
                      </Label>
                      <Select
                        value={selectedSource ?? undefined}
                        onValueChange={(v) => setSelectedSource(v)}
                      >
                        <SelectTrigger className="w-[320px]">
                          <SelectValue placeholder="Select a source" />
                        </SelectTrigger>
                        <SelectContent>
                          {overhead.sources.map((s) => (
                            <SelectItem key={s} value={s}>
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {selectedSource && (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[120px]">Month</TableHead>
                            <TableHead>Overhead ({selectedSource})</TableHead>
                            <TableHead className="w-[180px]" />
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {fyMonths.map((m) => (
                            <OverheadMonthRow
                              key={`${selectedSource}-${m}`}
                              source={selectedSource}
                              month={m}
                              amount={overheadForSource.get(m) ?? 0}
                              savedField={savedField}
                              onSave={saveOverhead}
                              onBulk={() => bulkOverhead(selectedSource, m)}
                            />
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="gpm-costs">
            <GpmCostSection />
          </TabsContent>
        </Tabs>
      </div>
    </TooltipProvider>
  );
}

function ConsultingMonthRow({
  row,
  savedField,
  onSave,
  onBulk,
}: {
  row: ConsultingRow;
  savedField: string | null;
  onSave: (month: string, er: number, def: number, field: string) => Promise<void>;
  onBulk: () => Promise<void>;
}) {
  const [er, setEr] = useState(formatMoneyInput(row.engagementRingFee));
  const [def, setDef] = useState(formatMoneyInput(row.defaultFee));
  const [bulking, setBulking] = useState(false);

  useEffect(() => setEr(formatMoneyInput(row.engagementRingFee)), [row.engagementRingFee]);
  useEffect(() => setDef(formatMoneyInput(row.defaultFee)), [row.defaultFee]);

  const commitEr = () => {
    const parsed = parseMoney(er);
    if (parsed == null || parsed === row.engagementRingFee) return;
    onSave(row.month, parsed, row.defaultFee, `consulting-${row.month}-er`);
  };
  const commitDef = () => {
    const parsed = parseMoney(def);
    if (parsed == null || parsed === row.defaultFee) return;
    onSave(row.month, row.engagementRingFee, parsed, `consulting-${row.month}-other`);
  };

  return (
    <TableRow>
      <TableCell className="font-medium">{formatMonthLabel(row.month)}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">$</span>
          <Input
            type="text"
            inputMode="decimal"
            value={er}
            onChange={(e) => setEr(e.target.value)}
            onBlur={commitEr}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
            className={savedFieldClass(savedField, `consulting-${row.month}-er`)}
          />
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">$</span>
          <Input
            type="text"
            inputMode="decimal"
            value={def}
            onChange={(e) => setDef(e.target.value)}
            onBlur={commitDef}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
            className={savedFieldClass(savedField, `consulting-${row.month}-other`)}
          />
        </div>
      </TableCell>
      <TableCell>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={bulking}
              onClick={async () => {
                setBulking(true);
                try {
                  await onBulk();
                } finally {
                  setBulking(false);
                }
              }}
            >
              {bulking ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
              <span className="ml-1.5 text-xs">Apply to rest</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            Copies this row's values to all following months in the fiscal year.
          </TooltipContent>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

function GpmCostSection() {
  const [engagementRing, setEngagementRing] = useState("");
  const [other, setOther] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch("/api/deal-classification/gpm-costs")
      .then((res) => res.json())
      .then((data: { engagementRing: number; other: number }) => {
        setEngagementRing(String(data.engagementRing));
        setOther(String(data.other));
      })
      .catch(() => setError("Failed to load GPM costs"))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setSaved(false);
    setSaving(true);
    try {
      const res = await fetch("/api/deal-classification/gpm-costs", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          engagementRing: Number(engagementRing),
          other: Number(other),
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.error ?? "Failed to save");
        return;
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError("An unexpected error occurred");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-navy/10">
            <Calculator className="h-4 w-4 text-brand-navy" />
          </div>
          <div>
            <CardTitle className="font-serif">GPM Cost Per Deal</CardTitle>
            <CardDescription>
              Configure the cost deducted from Net Sales to calculate Gross Profit Margin. GPM = Net
              Sales &minus; Cost, summed across closed deals.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading...
          </div>
        ) : (
          <form onSubmit={handleSave} className="max-w-md space-y-4">
            <div className="space-y-2">
              <Label htmlFor="gpm-cost-er">Engagement Ring Cost</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  $
                </span>
                <Input
                  id="gpm-cost-er"
                  type="number"
                  min="0"
                  step="1"
                  value={engagementRing}
                  onChange={(e) => setEngagementRing(e.target.value)}
                  className="pl-7"
                  required
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Deducted from each Engagement Ring deal's net price.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="gpm-cost-other">All Other Deals Cost</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  $
                </span>
                <Input
                  id="gpm-cost-other"
                  type="number"
                  min="0"
                  step="1"
                  value={other}
                  onChange={(e) => setOther(e.target.value)}
                  className="pl-7"
                  required
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Deducted from all non–Engagement Ring deals' net price.
              </p>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <Button type="submit" disabled={saving || !engagementRing || !other} size="sm">
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : saved ? (
                <Check className="mr-2 h-4 w-4" />
              ) : null}
              {saved ? "Saved" : "Save Changes"}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

function OverheadMonthRow({
  source,
  month,
  amount,
  savedField,
  onSave,
  onBulk,
}: {
  source: string;
  month: string;
  amount: number;
  savedField: string | null;
  onSave: (source: string, month: string, amount: number, field: string) => Promise<void>;
  onBulk: () => Promise<void>;
}) {
  const [val, setVal] = useState(formatMoneyInput(amount));
  const [bulking, setBulking] = useState(false);

  useEffect(() => setVal(formatMoneyInput(amount)), [amount]);

  const commit = () => {
    const parsed = parseMoney(val);
    if (parsed == null || parsed === amount) return;
    onSave(source, month, parsed, `overhead-${source}-${month}`);
  };

  return (
    <TableRow>
      <TableCell className="font-medium">{formatMonthLabel(month)}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1 max-w-xs">
          <span className="text-muted-foreground">$</span>
          <Input
            type="text"
            inputMode="decimal"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
            className={savedFieldClass(savedField, `overhead-${source}-${month}`)}
          />
        </div>
      </TableCell>
      <TableCell>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={bulking}
              onClick={async () => {
                setBulking(true);
                try {
                  await onBulk();
                } finally {
                  setBulking(false);
                }
              }}
            >
              {bulking ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
              <span className="ml-1.5 text-xs">Apply to rest</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Copies this amount to all following months for {source}.</TooltipContent>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}
